import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { PublicHeader } from '@/components/public/PublicHeader';
import { AssessmentResultsView } from '@/components/assessment-engine/AssessmentResultsView';
import { TakeAssessmentCampaignTitle } from '@/components/assessment-engine/TakeAssessmentCampaignTitle';
import {
  AE_CARD,
  AE_LINK,
  AE_MUTED,
  AE_PUBLIC_MAIN,
  AE_PUBLIC_PAGE,
  AE_SECTION_TITLE,
  AE_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeBranding, AeInstantResults, AePublicCampaign } from '@/types/assessmentEngine';

/** P8 — instant individual results after assessment complete (REQ-AE-063, 073). */
export const AssessmentResultsPage: React.FC = () => {
  const { t } = useLanguage();
  const { linkToken, responseId } = useParams<{ linkToken: string; responseId: string }>();
  const [results, setResults] = useState<AeInstantResults | null>(null);
  const [campaign, setCampaign] = useState<AePublicCampaign | null>(null);
  const [periodNotCurrent, setPeriodNotCurrent] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!linkToken || !responseId) return;
    setPeriodNotCurrent(false);
    setLoadFailed(false);
    Promise.all([
      assessmentEngineService.getInstantResults(linkToken, responseId),
      assessmentEngineService.getPublicCampaign(linkToken).catch(() => null),
    ])
      .then(([instant, publicCampaign]) => {
        setResults(instant);
        setCampaign(publicCampaign);
      })
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        if (msg === 'PERIOD_NOT_CURRENT') {
          setPeriodNotCurrent(true);
          return;
        }
        setLoadFailed(true);
        toast.error(t('assessmentEngine.results.failedLoad'));
      });
  }, [linkToken, responseId, t]);

  const shell = (content: React.ReactNode) => (
    <div className={AE_PUBLIC_PAGE}>
      <PublicHeader />
      <main className={AE_PUBLIC_MAIN}>{content}</main>
    </div>
  );

  if (periodNotCurrent) {
    return shell(
      <div className={AE_CARD}>
        <h1 className={`${AE_TITLE} mb-2`}>{t('assessmentEngine.results.periodNotCurrent')}</h1>
        <p className={`${AE_MUTED} mb-4`}>{t('assessmentEngine.results.periodNotCurrentHint')}</p>
        <Link to={`/assessment/take/${linkToken}`} className={AE_LINK}>
          {t('assessmentEngine.results.startCurrentPeriod')}
        </Link>
      </div>
    );
  }

  if (!results) {
    return shell(
      <p className={AE_MUTED}>
        {loadFailed ? t('assessmentEngine.results.failedLoad') : t('assessmentEngine.results.loading')}
      </p>
    );
  }

  const branding = (campaign?.branding ?? {}) as AeBranding;

  return shell(
    <div className="space-y-6">
      <TakeAssessmentCampaignTitle campaignName={campaign?.name} branding={branding} />

      <div className={AE_CARD}>
        <h1 className={`${AE_TITLE} mb-2`}>{t('assessmentEngine.results.title')}</h1>
        <p className="mb-4 text-sm text-[var(--color-surface-text)]">
          {t('assessmentEngine.results.subtitle')}
        </p>
        <AssessmentResultsView results={results} />
      </div>

      {results.progression.length > 0 && (
        <div className={AE_CARD}>
          <h2 className={`${AE_SECTION_TITLE} mb-3`}>{t('assessmentEngine.results.progression')}</h2>
          <ul className="space-y-2 text-sm">
            {results.progression.map((p) => (
              <li key={p.response_id} className="border-b border-[var(--color-border)] pb-2">
                <span className="font-medium text-[var(--color-surface-text)]">{p.campaign_name}</span>
                {p.completed_at && (
                  <span className="text-[var(--color-secondary-text)] ml-2">
                    {new Date(p.completed_at).toLocaleDateString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-[var(--color-secondary-text)]">{t('assessmentEngine.results.aiAssistive')}</p>
      <Link to={`/assessment/take/${linkToken}`} className={AE_LINK}>
        {t('assessmentEngine.results.backToAssessment')}
      </Link>
    </div>
  );
};
